import React, { useState } from 'react';
import { Mail, CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { Analytics } from '../utils/analytics';

interface EmailVerificationPromptProps {
  email: string;
  onBackToLogin: () => void;
}

const EmailVerificationPrompt: React.FC<EmailVerificationPromptProps> = ({
  email,
  onBackToLogin
}) => {
  const { t } = useLocalization();
  const { sendVerificationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      await sendVerificationEmail();
      setResendSuccess(true);
      Analytics.verificationEmailSent(email);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
    } catch (error: any) {
      setError(error.message || t.errors.general.unknownError);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t.auth.emailVerification.title}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {t.auth.emailVerification.message.replace('{email}', email)}
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">{t.auth.emailVerification.checkEmail}</p>
                <p>{t.auth.emailVerification.followInstructions}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t.auth.emailVerification.sending}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  {t.auth.emailVerification.resendEmail}
                </>
              )}
            </button>

            <button
              onClick={onBackToLogin}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.auth.emailVerification.backToLogin}
            </button>
          </div>

          {resendSuccess && (
            <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 text-sm">
                <CheckCircle className="w-4 h-4" />
                {t.auth.emailVerification.emailSent}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="text-red-800 text-sm">
                {error}
              </div>
            </div>
          )}

          <div className="mt-6 text-xs text-gray-500">
            <p>{t.auth.emailVerification.helpText}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPrompt;
